import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import MarqueeBanner from "./MarqueeBanner";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { splitSiteName } from "@/lib/siteName";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { language, site } = useSiteSettings();
  const brand = splitSiteName(site.site_name);

  const navLinks = [
    { label: t("nav.home"), path: "/" },
    { label: t("nav.engines"), path: "/engines" },
    { label: t("nav.transmissions"), path: "/transmissions" },
    { label: t("nav.bodyParts"), path: "/body-parts" },
    { label: t("nav.testimonials"), path: "/testimonials" },
    { label: t("nav.contact"), path: "/contact-us" },
  ];

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <>
      <MarqueeBanner />
      <header className="sticky top-0 z-50 w-full bg-brand-dark text-white">
        <div className="w-full px-4 md:px-6">
          <div className="flex h-16 md:h-20 items-center justify-between gap-4">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-gold text-brand-dark">
                <i className="ri-car-line text-2xl"></i>
              </span>
              {brand.second ? (
                <span className="leading-tight">
                  <span className="font-heading text-lg md:text-xl font-semibold tracking-wide uppercase block">
                    {brand.first}
                  </span>
                  <span className="font-heading text-[11px] md:text-xs tracking-[0.25em] uppercase text-brand-gold block">
                    {brand.second}
                  </span>
                </span>
              ) : (
                <span className="font-heading text-lg md:text-xl font-semibold tracking-wide uppercase">
                  {brand.first}
                </span>
              )}
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${
                    isActive(link.path)
                      ? "text-brand-gold"
                      : "text-white/80 hover:text-brand-gold"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-2">
              {language.show_switch && <LanguageSwitcher />}
              <Link
                to="/contact-us"
                className="flex items-center gap-2 rounded-md bg-brand-gold px-5 py-2.5 text-sm font-semibold uppercase tracking-wide text-brand-dark transition hover:bg-white hover:text-brand-dark whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                {t("nav.getQuote")}
              </Link>
            </div>

            {/* Mobile toggle */}
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="md:hidden flex h-10 w-10 items-center justify-center rounded-md text-white"
              aria-label={t("nav.menu")}
            >
              <i className={`text-2xl ${open ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="md:hidden bg-brand-dark border-t border-white/10">
            <div className="px-4 py-4 flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setOpen(false)}
                  className={`px-4 py-3 text-sm font-medium uppercase tracking-wide rounded-md transition-colors ${
                    isActive(link.path)
                      ? "bg-white/5 text-brand-gold"
                      : "text-white/80 hover:bg-white/5"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="mt-2 flex items-center justify-between gap-3">
                {language.show_switch && <LanguageSwitcher />}
                <Link
                  to="/contact-us"
                  onClick={() => setOpen(false)}
                  className="flex flex-1 items-center justify-center gap-2 rounded-md bg-brand-gold px-5 py-3 text-sm font-semibold uppercase tracking-wide text-brand-dark"
                >
                  <i className="ri-phone-line"></i>
                  {t("nav.getQuote")}
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}